// Local Intelligence — every call the /local-intelligence page makes.
//
// Envelope architecture (§2.4.0): one fn_li_page call returns every panel with its
// sentence already written; the Ask console, drills and staff actions call their own
// functions on interaction. All calls go through the shared conversationRpc wrapper
// (the same one searchConsole.js uses): at most four in flight, two retries with
// jitter on transport failures, and a returned { error: true, message } is thrown.
// No component on the page imports the Supabase client.
import { conversationRpc } from './conversation';
import { supabase } from '../lib/supabase';

const rpc = (name, params, signal) => conversationRpc(name, params, 'Local Intelligence', signal);

export const getPage = (clientId, signal) => rpc('fn_li_page', { p_client_id: clientId }, signal);

export const askAnswer = (clientId, a) => rpc('fn_li_answer', {
  p_client_id: clientId,
  p_need: a.need,
  p_at: a.at ?? null,
  p_party: a.party ?? {},
  p_limit: a.limit ?? 3,
  p_audience: 'staff',
  p_surface: 'dashboard',
  p_log: true,
});

export const getPlace = (clientId, entityId) =>
  rpc('fn_li_place_detail', { p_client_id: clientId, p_entity_id: entityId });

export const getDrill = (clientId, what, value = null, limit = 50, offset = 0) =>
  rpc('fn_li_drill', { p_client_id: clientId, p_what: what, p_value: value, p_limit: limit, p_offset: offset });

export const getStayPlan = (clientId, arrival, nights, party) =>
  rpc('fn_li_stay_plan', { p_client_id: clientId, p_arrival: arrival, p_nights: nights, p_party: party ?? {} });

export const curate = (clientId, entityId, action, value = null) =>
  rpc('fn_li_curate', { p_client_id: clientId, p_entity_id: entityId, p_action: action, p_value: value });

export const createRequest = (clientId, r) => rpc('fn_li_request_create', {
  p_client_id: clientId,
  p_entity_id: r.entityId,
  p_party_size: r.partySize,
  p_requested_for: r.at,
  p_note: r.note ?? null,
});

export const recordRequest = (clientId, requestId, outcome, detail = null) =>
  rpc('fn_li_request_record', { p_client_id: clientId, p_request_id: requestId, p_outcome: outcome, p_detail: detail });

export const recordOutcome = (clientId, recommendationId, outcome) =>
  rpc('fn_li_record_outcome', { p_client_id: clientId, p_recommendation_id: recommendationId, p_outcome: outcome });

// ── Send to guest ──────────────────────────────────────────────────────────────────
// Guests here now or arriving tomorrow (or matching a name or site), the send itself
// (adds the place to that stay's folio and records the share), and the park-page link
// and QR code for a guest who has no folio link.
export const getDeskStays = (clientId, query) =>
  rpc('fn_li_desk_stays', { p_client_id: clientId, p_query: query || null, p_limit: 12 });

export const sendToStay = (clientId, stayId, entityId, recommendationId = null) =>
  rpc('fn_li_send_to_stay', { p_client_id: clientId, p_stay_id: stayId, p_entity_id: entityId, p_recommendation_id: recommendationId });

export const getPlaceShare = (clientId, entityId) =>
  rpc('fn_li_place_share', { p_client_id: clientId, p_entity_id: entityId });

// ── Desk calling (the shared cv-voice service) ─────────────────────────────────────
// Settings and call status are database reads; placing a call and checking the park's
// own number go through the cv-voice edge function, which talks to Twilio.
export const getCallSettings = (clientId) => rpc('fn_cvv_settings', { p_client_id: clientId });

export const setDeskPhone = (clientId, number, label = null) =>
  rpc('fn_cvv_desk_set', { p_client_id: clientId, p_number: number, p_label: label });

export const getCallStatus = (clientId, callId) =>
  rpc('fn_cvv_call_get', { p_client_id: clientId, p_call_id: callId });

const voice = async (body) => {
  const { data, error } = await supabase.functions.invoke('cv-voice', { body });
  if (error) {
    let message = error.message;
    try {
      const j = await error.context?.json?.();
      if (j?.message) message = j.message;
    } catch (e) {
      // the edge function answered without a JSON body; keep the transport message
    }
    throw { message: message || 'Desk calling is unavailable right now.' };
  }
  if (data && data.ok === false) throw { message: data.message || 'The call could not be placed.', reason: data.reason };
  return data;
};

export const startDeskCall = (clientId, entityId) =>
  voice({ action: 'call', client_id: clientId, source_product: 'local_intelligence', source_ref: entityId });

export const startNumberCheck = (clientId) => voice({ action: 'verify_start', client_id: clientId });

export const checkNumber = (clientId) => voice({ action: 'verify_check', client_id: clientId });
